import React, { useState, useEffect } from "react";
import { Mail, Send, Users, Filter, RefreshCw, Inbox as InboxIcon, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AppLayout from "../../components/AppLayout";

export const BROADCAST_DISABLED_NOTICE = "Broadcast disabled pending compliance fix";

export const EMAIL_VIEWS = ["compose", "inbox", "sent"];

// Standalone it keeps its own tabs; inside the Email section the section's
// ?tab= picks the view (`view` prop) and this page hides its own header/tabs.
export default function EmailManagement({ view }) {
  const [localTab, setTab] = useState("compose"); // compose | inbox | sent
  const embedded = EMAIL_VIEWS.includes(view);
  const tab = embedded ? view : localTab;
  const [subscribers, setSubscribers] = useState([]);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [emailType, setEmailType] = useState("single");
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [tierFilter, setTierFilter] = useState("all");
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");

  useEffect(() => {
    loadSubscribers();
  }, []);

  useEffect(() => {
    if (tab === "inbox") loadInbox();
    if (tab === "sent") loadSentEmails();
    
    // Listen for data changes (background email syncs)
    const handleDataChanged = (e) => {
      if (e.detail?.source === 'email_sync' || e.detail?.type === 'emails' || e.detail?.type === 'inbox') {
        if (tab === "inbox") loadInbox();
        if (tab === "sent") loadSentEmails();
      }
    };
    
    window.addEventListener('gio:data-changed', handleDataChanged);
    
    return () => {
      window.removeEventListener('gio:data-changed', handleDataChanged);
    };
  }, [tab]);

  const loadSubscribers = async () => {
    try {
      const res = await api.get("/api/subscriptions/admin/all");
      setSubscribers(res.data.filter(s => s.status === "active"));
    } catch (err) {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };

  const loadInbox = async () => {
    try {
      const res = await api.get("/api/messages/inbox", { params: { limit: 50 } });
      setInboxMessages(res.data.messages || []);
    } catch (err) {
      console.error("Failed to load inbox:", err);
    }
  };

  const loadSentEmails = async () => {
    try {
      const res = await api.get("/api/emails/logs");
      setSentEmails(res.data || []);
    } catch (err) {
      console.error("Failed to load sent emails:", err);
    }
  };

  const handleSendEmail = async () => {
    if (!subject.trim() || !htmlBody.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }

    if (emailType === "single" && selectedEmails.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }

    setSending(true);
    try {
      if (emailType === "broadcast") {
        // Server answers 503 "Broadcast disabled pending compliance fix"; never fake success.
        toast.error(BROADCAST_DISABLED_NOTICE);
        return;
      } else {
        await api.post("/api/emails/send", {
          to: selectedEmails,
          subject,
          html_body: htmlBody,
        });
        toast.success(`Email sent to ${selectedEmails.length} recipient(s)!`);
      }
      
      // Reset form
      setSubject("");
      setHtmlBody("");
      setSelectedEmails([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const toggleSelectEmail = (email) => {
    if (selectedEmails.includes(email)) {
      setSelectedEmails(selectedEmails.filter(e => e !== email));
    } else {
      setSelectedEmails([...selectedEmails, email]);
    }
  };

  const filteredSubscribers = subscribers.filter(s => 
    tierFilter === "all" || s.tier === tierFilter
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className={embedded ? "max-w-7xl" : "max-w-7xl mx-auto p-6"}>
        {!embedded && (<>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">📧 Email Management</h1>
          <p className="text-zinc-400">Manage emails and messages</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-white/10">
          <button
            onClick={() => setTab("compose")}
            className={`px-6 py-3 font-semibold transition flex items-center gap-2 ${
              tab === "compose"
                ? "text-purple-400 border-b-2 border-purple-400"
                : "text-zinc-500 hover:text-zinc-300"
            }`}>
            <Mail size={18} />
            Compose
          </button>
          <button
            onClick={() => setTab("inbox")}
            className={`px-6 py-3 font-semibold transition flex items-center gap-2 ${
              tab === "inbox"
                ? "text-purple-400 border-b-2 border-purple-400"
                : "text-zinc-500 hover:text-zinc-300"
            }`}>
            <InboxIcon size={18} />
            Inbox
            {inboxMessages.length > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-purple-600 text-white text-xs">
                {inboxMessages.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("sent")}
            className={`px-6 py-3 font-semibold transition flex items-center gap-2 ${
              tab === "sent"
                ? "text-purple-400 border-b-2 border-purple-400"
                : "text-zinc-500 hover:text-zinc-300"
            }`}>
            <Send size={18} />
            Sent
          </button>
        </div>
        </>)}

        {/* Tab Content */}
        {tab === "compose" && renderComposeTab()}
        {tab === "inbox" && renderInboxTab()}
        {tab === "sent" && renderSentTab()}
      </div>
    </AppLayout>
  );

  function renderComposeTab() {
    return (
      <>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(113, 113, 122, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Total Active</div>
            <div className="text-3xl font-bold text-white">{subscribers.length}</div>
          </div>
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(236, 72, 153, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">Members</div>
            <div className="text-3xl font-bold text-pink-400">
              {subscribers.filter(s => s.tier === "member").length}
            </div>
          </div>
          <div className="p-6 rounded-xl border bg-zinc-900/50" style={{ borderColor: "rgba(168, 85, 247, 0.2)" }}>
            <div className="text-sm text-zinc-400 mb-1">VIP</div>
            <div className="text-3xl font-bold text-purple-400">
              {subscribers.filter(s => s.tier === "vip").length}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Email Composer */}
          <div className="lg:col-span-2 bg-zinc-900/50 border border-white/5 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-6">Compose Email</h2>

            {/* Email Type Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-400 mb-3">Email Type</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setEmailType("single")}
                  className={`flex-1 py-3 rounded-lg font-semibold transition ${
                    emailType === "single"
                      ? "bg-purple-600 text-white"
                      : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                  }`}>
                  Single / Selected
                </button>
                <button
                  onClick={() => setEmailType("broadcast")}
                  className={`flex-1 py-3 rounded-lg font-semibold transition ${
                    emailType === "broadcast"
                      ? "bg-purple-600 text-white"
                      : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                  }`}>
                  Broadcast to All
                </button>
              </div>
            </div>

            {emailType === "broadcast" && (
              <div role="alert" data-testid="broadcast-disabled-notice" className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                {BROADCAST_DISABLED_NOTICE}. Single / selected transactional emails still work.
              </div>
            )}

            {/* Tier Filter (for broadcast) */}
            {emailType === "broadcast" && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-zinc-400 mb-3">Target Audience</label>
                <select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white">
                  <option value="all">All Subscribers</option>
                  <option value="member">Members Only</option>
                  <option value="vip">VIP Only</option>
                </select>
              </div>
            )}

            {/* Subject */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-400 mb-2">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject..."
                className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 outline-none"
              />
            </div>

            {/* Message Body */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-zinc-400 mb-2">Message</label>
              <textarea
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                placeholder="Write your message here... (HTML supported)"
                className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:border-purple-500 outline-none min-h-[300px] resize-none font-mono text-sm"
              />
              <p className="text-xs text-zinc-500 mt-2">HTML formatting is supported</p>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSendEmail}
              disabled={sending || emailType === "broadcast"}
              title={emailType === "broadcast" ? BROADCAST_DISABLED_NOTICE : undefined}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition inline-flex items-center justify-center gap-2">
              {sending ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  {emailType === "broadcast" ? "Broadcast disabled" : `Send to ${selectedEmails.length} Selected`}
                </>
              )}
            </button>
          </div>

          {/* Subscriber List (for single emails) */}
          {emailType === "single" && (
            <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white">Select Recipients</h3>
                <span className="text-xs text-zinc-500">{selectedEmails.length} selected</span>
              </div>

              {/* Tier Filter */}
              <div className="mb-4">
                <select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white">
                  <option value="all">All Tiers</option>
                  <option value="member">Members</option>
                  <option value="vip">VIP</option>
                </select>
              </div>

              {/* Subscriber Checkboxes */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredSubscribers.map((sub) => (
                  <label
                    key={sub.email}
                    className="flex items-center gap-3 p-3 bg-zinc-800/30 hover:bg-zinc-800/50 rounded-lg cursor-pointer transition">
                    <input
                      type="checkbox"
                      checked={selectedEmails.includes(sub.email)}
                      onChange={() => toggleSelectEmail(sub.email)}
                      className="w-4 h-4 text-purple-600 bg-zinc-700 border-zinc-600 rounded focus:ring-purple-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{sub.email}</div>
                      <div className={`text-xs ${sub.tier === "vip" ? "text-purple-400" : "text-pink-400"}`}>
                        {sub.tier.toUpperCase()}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  function renderInboxTab() {
    return (
      <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Inbox</h2>
          <button
            onClick={loadInbox}
            className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm flex items-center gap-2">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {inboxMessages.length === 0 ? (
          <div className="text-center py-12">
            <InboxIcon size={48} className="mx-auto mb-4 text-zinc-600" />
            <p className="text-zinc-400">No messages in inbox</p>
          </div>
        ) : (
          <div className="space-y-3">
            {inboxMessages.map((msg, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg bg-zinc-900/50 border border-white/5 hover:border-purple-500/50 transition">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-semibold text-white">{msg.from || "User"}</div>
                  <div className="text-xs text-zinc-500">{msg.timestamp || "Recent"}</div>
                </div>
                <div className="text-sm text-zinc-400 mb-2">{msg.subject || "No subject"}</div>
                <div className="text-sm text-zinc-300">{msg.message || msg.body}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderSentTab() {
    return (
      <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Sent Emails</h2>
          <button
            onClick={loadSentEmails}
            className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm flex items-center gap-2">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {sentEmails.length === 0 ? (
          <div className="text-center py-12">
            <Send size={48} className="mx-auto mb-4 text-zinc-600" />
            <p className="text-zinc-400">No sent emails yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sentEmails.map((email, idx) => (
              <div
                key={idx}
                className="p-4 rounded-lg bg-zinc-900/50 border border-white/5">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-semibold text-white">{email.subject}</div>
                  <div className="text-xs text-zinc-500 flex items-center gap-1">
                    <CheckCircle size={14} className="text-green-500" />
                    {email.sent_at || "Sent"}
                  </div>
                </div>
                <div className="text-sm text-zinc-400 mb-2">
                  To: {email.recipient_count || email.recipients?.length || 0} recipient(s)
                </div>
                <div className="text-sm text-zinc-500">
                  {email.preview || email.body?.substring(0, 100)}...
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
